import { loadManagedUserProfile } from '@/services';
import type { IManagedUserSummary } from '@/types';
import { useCallback } from 'react';

export interface UseManagedUserDataParams {
  /** The ID of the manager for whom the data is being fetched */
  managerId: string;
  /** The ID of the active user being managed */
  managerActiveUserId: string;
  /** Callback to set the active managed user's data */
  setManagerActiveUser: (user: IManagedUserSummary) => void;
  /** Callback to set the loading state for the active managed user's data */
  setManagerActiveUserIsLoading: (isLoading: boolean) => void;
}

/**
 * Custom hook to fetch and manage the active managed user's profile data for the manager dashboard.
 *
 * This hook encapsulates the logic for:
 *  - Fetching the managed user's score profile from the API using the provided params.
 *  - Mapping the raw API response into the frontend-friendly `IManagedUserSummary` format.
 *  - Managing the loading state during the fetch operation.
 */
export const useManagedUserData = ({
  managerId,
  managerActiveUserId,
  setManagerActiveUser,
  setManagerActiveUserIsLoading,
}: UseManagedUserDataParams) => {
  const fetchManagedUserData = useCallback(async () => {
    // Set loading state to true at the start of the fetch operation.
    setManagerActiveUserIsLoading(true);

    try {
      // Delegate profile loading + mapping to management service.
      const result = await loadManagedUserProfile({
        managerId,
        userId: managerActiveUserId,
      });

      // Update the active managed user's data in the global store.
      setManagerActiveUser(result);
    } catch (error) {
      // Log any errors that occur during the fetch operation for debugging purposes.
      console.error('Error fetching manager user profile:', error);
    } finally {
      // Ensure that the loading state is set to false after the operation completes,
      setManagerActiveUserIsLoading(false);
    }
  }, [
    // Dependencies for the useCallback hook to ensure it only changes when necessary.
    managerActiveUserId,
    managerId,
    setManagerActiveUser,
    setManagerActiveUserIsLoading,
  ]);

  // ================================================
  // Expose the fetch function for use in components
  // ================================================
  return {
    /**
     * Fetches the managed user's profile data from the API, maps it to the frontend format,
     * and updates the global store with the active user's data. It also manages the loading
     * state throughout the operation to provide feedback to the UI.
     */
    fetchManagedUserData,
  };
};
